import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  TextField,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Grid,
  Card,
  Stack,
  InputAdornment
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  AccountBalanceWallet,
  CalendarMonth,
  ReceiptLong,
  RestartAlt
} from "@mui/icons-material";
import Swal from "sweetalert2";
import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  getDocs,
  orderBy,
  query,
  Timestamp
} from "firebase/firestore";
import { db } from "../../firebase";

function Rdsaving() {
  const [data, setData] = useState([]);
  const [filtered, setFiltered] = useState([]);

  // Filters state
  const [search, setSearch] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [open, setOpen] = useState(false);

  const [form, setForm] = useState({
    id: "",
    depositDate: new Date().toISOString().split("T")[0],
    bankName: "",
    amount: "",
    description: "",
    note: ""
  });

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [search, fromDate, toDate, data]);

  const loadData = async () => {
    try {
      const q = query(collection(db, "rd_savings"), orderBy("depositDate", "desc"));
      const snap = await getDocs(q);
      const arr = snap.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setData(arr);
      setFiltered(arr);
    } catch (err) {
      console.error(err);
    }
  };

  const applyFilters = () => {
    let result = [...data];

    if (search.trim() !== "") {
      result = result.filter(item =>
        JSON.stringify(item).toLowerCase().includes(search.toLowerCase())
      );
    }
    if (fromDate) {
      result = result.filter(item => item.depositDate >= fromDate);
    }
    if (toDate) {
      result = result.filter(item => item.depositDate <= toDate);
    }

    setFiltered(result);
    setPage(0);
  };

  const handleResetFilters = () => {
    setSearch("");
    setFromDate("");
    setToDate("");
  };

  const resetForm = () => {
    setForm({
      id: "",
      depositDate: new Date().toISOString().split("T")[0],
      bankName: "",
      amount: "",
      description: "",
      note: ""
    });
  };

  const handleSave = async () => {
    if (!form.bankName || !form.amount) {
      Swal.fire("Error", "Bank Name and Amount are required", "error");
      return;
    }

    try {
      const payload = {
        depositDate: form.depositDate,
        bankName: form.bankName,
        amount: Number(form.amount),
        description: form.description,
        note: form.note,
        createdAt: Timestamp.now()
      };

      if (form.id) {
        await updateDoc(doc(db, "rd_savings", form.id), payload);
        Swal.fire("Success", "Record updated successfully", "success");
      } else {
        await addDoc(collection(db, "rd_savings"), payload);
        Swal.fire("Success", "Record added successfully", "success");
      }

      setOpen(false);
      resetForm();
      loadData();
    } catch (err) {
      Swal.fire("Error", err.message, "error");
    }
  };

  const handleEdit = (row) => {
    setForm({
      id: row.id,
      depositDate: row.depositDate,
      bankName: row.bankName,
      amount: row.amount,
      description: row.description || "",
      note: row.note || ""
    });
    setOpen(true);
  };

  const handleDelete = async (id) => {
    const confirm = await Swal.fire({
      title: "Are you sure?",
      text: "You will not be able to recover this record!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!"
    });

    if (!confirm.isConfirmed) return;

    await deleteDoc(doc(db, "rd_savings", id));
    Swal.fire("Deleted", "Record deleted successfully", "success");
    loadData();
  };

  const overallTotal = data.reduce((sum, item) => sum + Number(item.amount || 0), 0);
  const filteredTotal = filtered.reduce((sum, item) => sum + Number(item.amount || 0), 0);

  return (
    <Box p={{ xs: 1.5, md: 2 }} sx={{ backgroundColor: "#f8fafc", minHeight: "100vh" }}>
      
      {/* Header Section */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} gap={2}>
        <Box>
          <Typography variant="subtitle1" fontWeight="800" sx={{ color: "#0f172a", lineHeight: 1.2 }}>
            RD Savings Management
          </Typography>
          <Typography variant="caption" color="textSecondary">
            Track and manage your small daily deposit accounts.
          </Typography>
        </Box>
        <Button
          variant="contained"
          size="small"
          disableElevation
          startIcon={<AddIcon sx={{ fontSize: "1rem !important" }} />}
          onClick={() => {
            resetForm();
            setOpen(true);
          }}
          sx={{
            borderRadius: "6px",
            textTransform: "none",
            fontWeight: "600",
            fontSize: "0.8rem",
            px: 2,
            py: 0.5,
            background: "linear-gradient(135deg, #3a7bd5 0%, #3a6073 100%)",
          }}
        >
          Add Deposit
        </Button>
      </Box>

      {/* Summary Cards */}
      <Grid className="row" spacing={1.5} mb={2}>
        <Grid className="col-sm-4 mb-2" item xs={12} sm={4}>
          <Card elevation={0} sx={{ borderRadius: "8px", border: "1px solid #e2e8f0" }}>
            <Box p={1.5} display="flex" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography color="textSecondary" variant="caption" fontWeight="600">Total Saved</Typography>
                <Typography variant="subtitle1" fontWeight="700" color="primary.main">
                  ₹ {overallTotal.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                </Typography>
              </Box>
              <Box sx={{ p: 1, borderRadius: "6px", backgroundColor: "#e3f2fd", color: "#1e88e5", display: "flex" }}>
                <AccountBalanceWallet fontSize="small" />
              </Box>
            </Box>
          </Card>
        </Grid>
        <Grid className="col-sm-4 mb-2" item xs={12} sm={4}>
          <Card elevation={0} sx={{ borderRadius: "8px", border: "1px solid #e2e8f0" }}>
            <Box p={1.5} display="flex" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography color="textSecondary" variant="caption" fontWeight="600">Filtered Total</Typography>
                <Typography variant="subtitle1" fontWeight="700" color="success.main">
                  ₹ {filteredTotal.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                </Typography>
              </Box>
              <Box sx={{ p: 1, borderRadius: "6px", backgroundColor: "#e8f5e9", color: "#43a047", display: "flex" }}>
                <CalendarMonth fontSize="small" />
              </Box>
            </Box>
          </Card>
        </Grid>
        <Grid className="col-sm-4 mb-2" item xs={12} sm={4}>
          <Card elevation={0} sx={{ borderRadius: "8px", border: "1px solid #e2e8f0" }}>
            <Box p={1.5} display="flex" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography color="textSecondary" variant="caption" fontWeight="600">Total Entries</Typography>
                <Typography variant="subtitle1" fontWeight="700" color="#0f172a">
                  {filtered.length} <span style={{ fontSize: "11px", fontWeight: "400", color: "#64748b" }}>items</span>
                </Typography>
              </Box>
              <Box sx={{ p: 1, borderRadius: "6px", backgroundColor: "#f3e5f5", color: "#8e24aa", display: "flex" }}>
                <ReceiptLong fontSize="small" />
              </Box>
            </Box>
          </Card>
        </Grid>
      </Grid>

      {/* Table Section */}
      <Paper elevation={0} sx={{ borderRadius: "8px", border: "1px solid #e2e8f0", overflow: "hidden" }}>
        
        {/* Filters */}
        <Box p={1.5} bgcolor="#fff" borderBottom="1px solid #f1f5f9">
          <Grid container spacing={1.5} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search anything here..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" sx={{ fontSize: "1.1rem" }} />
                    </InputAdornment>
                  )
                }}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "6px", fontSize: "0.8rem" } }}
              />
            </Grid>
            <Grid item xs={12} sm={4} md={2.5}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="Start Date"
                InputLabelProps={{ shrink: true }}
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "6px", fontSize: "0.8rem" } }}
              />
            </Grid>
            <Grid item xs={12} sm={4} md={2.5}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="End Date"
                InputLabelProps={{ shrink: true }}
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "6px", fontSize: "0.8rem" } }}
              />
            </Grid>
            <Grid item xs={12} sm={4} md={2}>
              <Button
                fullWidth
                size="small"
                variant="outlined"
                color="inherit"
                startIcon={<RestartAlt sx={{ fontSize: "1rem !important" }} />}
                onClick={handleResetFilters}
                sx={{ borderRadius: "6px", textTransform: "none", fontSize: "0.8rem", height: "34px", borderColor: "#cbd5e1", color: "#475569" }}
              >
                Clear Filters
              </Button>
            </Grid>
          </Grid>
        </Box>

        {/* Main Table */}
        <Box sx={{ overflowX: "auto" }}>
          <Table size="small">
            <TableHead sx={{ bgcolor: "#f8fafc" }}>
              <TableRow>
                <TableCell sx={{ fontWeight: "700", color: "#475569", py: 1, fontSize: "0.8rem", width: "60px" }}>S.No</TableCell>
                <TableCell sx={{ fontWeight: "700", color: "#475569", py: 1, fontSize: "0.8rem" }}>Date</TableCell>
                <TableCell sx={{ fontWeight: "700", color: "#475569", py: 1, fontSize: "0.8rem" }}>Bank Name</TableCell>
                <TableCell sx={{ fontWeight: "700", color: "#475569", py: 1, fontSize: "0.8rem" }}>Amount</TableCell>
                <TableCell sx={{ fontWeight: "700", color: "#475569", py: 1, fontSize: "0.8rem" }}>Description</TableCell>
                <TableCell sx={{ fontWeight: "700", color: "#475569", py: 1, fontSize: "0.8rem" }}>Note</TableCell>
                <TableCell align="center" sx={{ fontWeight: "700", color: "#475569", py: 1, fontSize: "0.8rem", width: "100px" }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody bgcolor="#fff">
              {filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4, color: "text.secondary", fontSize: "0.8rem" }}>
                    No records found matching your filters.
                  </TableCell>
                </TableRow>
              ) : (
                filtered
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row, index) => (
                    <TableRow key={row.id} hover sx={{ "& td": { py: 0.75, fontSize: "0.8rem" }, "&:last-child td, &:last-child th": { border: 0 } }}>
                      <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                      <TableCell sx={{ fontWeight: "500" }}>{row.depositDate}</TableCell>
                      <TableCell sx={{ color: "#334155", fontWeight: "600" }}>{row.bankName}</TableCell>
                      <TableCell sx={{ fontWeight: "700", color: "#16a34a" }}>
                        ₹ {Number(row.amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                      </TableCell>
                      <TableCell sx={{ color: "#64748b" }}>{row.description || "—"}</TableCell>
                      <TableCell sx={{ color: "#64748b" }}>{row.note || "—"}</TableCell>
                      <TableCell align="center">
                        <Stack direction="row" spacing={0.5} justifyContent="center">
                          <IconButton size="small" color="primary" onClick={() => handleEdit(row)} sx={{ p: 0.5 }}>
                            <EditIcon sx={{ fontSize: "1rem" }} />
                          </IconButton>
                          <IconButton size="small" color="error" onClick={() => handleDelete(row.id)} sx={{ p: 0.5 }}>
                            <DeleteIcon sx={{ fontSize: "1rem" }} />
                          </IconButton>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))
              )}
            </TableBody>
          </Table>
        </Box>

        <TablePagination
          component="div"
          count={filtered.length}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={(e, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          sx={{ borderTop: "1px solid #f1f5f9", "& .MuiTablePagination-toolbar": { minHeight: "40px" } }}
        />
      </Paper>

      {/* Input Modal Form */}
      <Dialog 
        open={open} 
        maxWidth="xs" 
        fullWidth
        PaperProps={{ sx: { borderRadius: "10px", p: 0.5 } }}
      >
        <DialogTitle sx={{ fontWeight: "800", fontSize: "1rem", pb: 1 }}>
          {form.id ? "✏️ Edit Deposit Record" : "➕ Add New Deposit"}
        </DialogTitle>
        <DialogContent dividers sx={{ p: 1.5 }}>
          <Box component="form" noValidate mt={1}>
            <Grid className="row" >
              <Grid className="col-sm-12 mb-3" item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  label="Deposit Date"
                  InputLabelProps={{ shrink: true }}
                  value={form.depositDate}
                  onChange={(e) => setForm({ ...form, depositDate: e.target.value })}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "6px", fontSize: "0.8rem" } }}
                />
              </Grid>
              <Grid className="col-sm-12 mb-3" item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label="Bank Name"
                  placeholder="Enter bank name"
                  value={form.bankName}
                  onChange={(e) => setForm({ ...form, bankName: e.target.value })}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "6px", fontSize: "0.8rem" } }}
                />
              </Grid>
              <Grid className="col-sm-12 mb-3" item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label="Amount (INR)"
                  placeholder="0.00"
                  value={form.amount}
                  onChange={(e) => setForm({ ...form, amount: e.target.value })}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "6px", fontSize: "0.8rem" } }}
                />
              </Grid>
              <Grid className="col-sm-12 mb-3" item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  label="Description"
                  placeholder="Enter details or purpose"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "6px", fontSize: "0.8rem" } }}
                />
              </Grid>
              <Grid className="col-sm-12 mb-3" item xs={12}>
                <TextField
                  fullWidth
                  size="small"
                  multiline
                  rows={2}
                  label="Extra Note"
                  placeholder="Enter extra remarks"
                  value={form.note}
                  onChange={(e) => setForm({ ...form, note: e.target.value })}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "6px", fontSize: "0.8rem" } }}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 1.5, gap: 0.5 }}>
          <Button 
            onClick={() => setOpen(false)} 
            color="inherit" 
            size="small"
            sx={{ textTransform: "none", fontWeight: "600", fontSize: "0.75rem" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            size="small"
            disableElevation
            onClick={handleSave}
            sx={{
              borderRadius: "6px",
              px: 2,
              textTransform: "none",
              fontWeight: "600",
              fontSize: "0.75rem",
              background: "linear-gradient(135deg, #3a7bd5 0%, #3a6073 100%)"
            }}
          >
            Save Data
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Rdsaving;